import { useEffect, useRef, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'

export type InfoTipProps = {
  /**
   * Tooltip body. Renders nothing when empty/undefined so call sites can
   * pass SAP `InfoText` straight through without their own guards.
   */
  text?: string
  /** Accessible name for the trigger button. */
  label?: string
  className?: string
}

/**
 * Small ⓘ trigger with a popover bubble — the rebuild of the legacy
 * `InfoTip.js` (antd info-circle + hover Tooltip). Built on Radix Popover
 * (already a dependency) rather than a hover-only tooltip so every input
 * works:
 *   - hover opens on desktop (with a short close delay so the pointer can
 *     travel into the bubble without it vanishing);
 *   - click/tap toggles for touch;
 *   - keyboard focus opens, blur/Esc closes.
 *
 * Used inside <label>-wrapped option rows — clicking a <button> descendant
 * of a label does NOT activate the label's input (interactive content is
 * excluded from label activation), so opening the tip never selects the
 * option.
 */
export function InfoTip({ text, label = 'More information', className = '' }: InfoTipProps) {
  const [open, setOpen] = useState(false)
  // Delayed close bridges the sideOffset gap between trigger and bubble —
  // an instant close on mouseleave would flicker shut mid-travel.
  const closeTimer = useRef<number | null>(null)
  const cancelClose = () => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }
  const openNow = () => {
    cancelClose()
    setOpen(true)
  }
  const closeSoon = () => {
    cancelClose()
    closeTimer.current = window.setTimeout(() => setOpen(false), 150)
  }
  useEffect(() => cancelClose, [])

  if (!text) return null
  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={label}
          className={['al-infotip', className].filter(Boolean).join(' ')}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
          onFocus={openNow}
          onBlur={closeSoon}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
          </svg>
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        {/* `xsmall` is the WP theme's global 15px type class — it reaches the
            portaled bubble because theme CSS is page-wide, unlike our
            .al-app-scoped rules. */}
        <Popover.Content
          className="al-infotip__content xsmall"
          side="bottom"
          sideOffset={6}
          collisionPadding={8}
          // Hover-opens must not yank focus into the bubble; the trigger
          // keeps focus, so blur/Esc continue to close as expected.
          onOpenAutoFocus={(e) => e.preventDefault()}
          onMouseEnter={cancelClose}
          onMouseLeave={closeSoon}
        >
          {text}
          <Popover.Arrow className="al-infotip__arrow" />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}
